// Require Editor CSS files.
import 'froala-editor/css/froala_style.min.css';
import 'froala-editor/css/froala_editor.pkgd.min.css';
// Import Froala plugins
import 'froala-editor/js/plugins/link.min.js';
import 'froala-editor/js/plugins/lists.min.js';
import 'froala-editor/js/plugins/char_counter.min.js';

import * as consts from 'consts/consts';
import { configForLocale } from 'locale/messages';
import React, {
  FC,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import FroalaEditor from 'react-froala-wysiwyg';
import { useIntl } from 'react-intl';
import { connect } from 'react-redux';
import Tribute from 'tributejs';
import { ReduxState } from 'types';
import { MetadataItem } from '../AgentsAdmin/useAgentMetadata';

interface OwnProps {
  value?: string;
  onChange?: (html: string) => void;
  resources: MetadataItem[];
  placeholder?: string;
  onResourceMention?: (resourceIds: string[]) => void;
}

interface ReduxProps {
  currentOrganizationId?: number;
}

type Props = OwnProps & ReduxProps;

const getResourceTributeOptions = (resources: MetadataItem[]) => ({
  noMatchTemplate: () =>
    '<div class="text-muted px-3 py-2">No matching resources</div>',
  collection: [
    {
      trigger: '@',
      selectClass: 'is-active',
      containerClass: 'tribute-container',
      selectTemplate: (item) => {
        const resource = item.original;
        return (
          '<span class="badge bg-primary-soft text-primary me-1" ' +
          'data-resource-id="' +
          resource.id +
          '" contenteditable="false">' +
          '@' +
          resource.name +
          '</span>&nbsp;'
        );
      },
      menuItemTemplate: (item) =>
        '<div class="d-flex flex-column py-1 px-2">' +
        '<strong>' +
        item.original.name +
        '</strong>' +
        (item.original.description
          ? '<small class="text-muted">' +
            item.original.description +
            '</small>'
          : '') +
        '</div>',
      lookup: (item) => item.name,
      fillAttr: 'name',
      values: (text, cb) => {
        const filtered = resources.filter((r) =>
          r.name.toLowerCase().includes(text.toLowerCase())
        );
        cb(filtered);
      },
      requireLeadingSpace: true,
      allowSpaces: true,
      menuItemLimit: 8,
      menuShowMinLength: 0,
    },
  ],
});

const extractResourceIds = (html: string): string[] => {
  const parser = new DOMParser();
  const doc = parser.parseFromString(html, 'text/html');
  const mentions = doc.querySelectorAll('[data-resource-id]');
  const ids = new Set<string>();
  mentions.forEach((el) => {
    const id = el.getAttribute('data-resource-id');
    if (id) ids.add(id);
  });
  return Array.from(ids);
};

const ResourceMentionEditor: FC<Props> = ({
  value = '',
  onChange,
  resources,
  placeholder,
  onResourceMention,
  currentOrganizationId,
}) => {
  const { locale } = useIntl();
  const ref = useRef({ editor: null });
  const [isFroalaInitialized, setIsFroalaInitialized] = useState(false);
  const [tributeIsSet, setTributeIsSet] = useState(false);
  const [model, setModel] = useState(value);

  useEffect(() => {
    setModel(value);
  }, [value]);

  const onModelChange = useCallback(
    (updatedModel: string) => {
      if (!isFroalaInitialized) return;
      if (model !== updatedModel) {
        setModel(updatedModel);
        if (onChange) {
          onChange(updatedModel);
        }
        if (onResourceMention) {
          onResourceMention(extractResourceIds(updatedModel));
        }
      }
    },
    [isFroalaInitialized, model, onChange, onResourceMention]
  );

  // Attach TributeJS after Froala initializes
  useEffect(() => {
    if (
      isFroalaInitialized &&
      // @ts-expect-error
      ref?.current?.editor?.data &&
      resources.length > 0 &&
      !tributeIsSet
    ) {
      const editor = ref.current.editor;
      const tribute = new Tribute(getResourceTributeOptions(resources));
      // @ts-expect-error
      tribute.attach(editor.el);
      setTributeIsSet(true);

      // @ts-expect-error
      editor.events.on(
        'keydown',
        (e) => {
          if (e.which === 13 && tribute.isActive) {
            return false;
          }
        },
        true
      );
    }
  }, [isFroalaInitialized, ref, resources, tributeIsSet]);

  const config = useMemo(
    () => ({
      language: configForLocale(locale).richTextEditor,
      attribution: false,
      enter: 2, // ENTER_BR
      pastePlain: true,
      fileUpload: false,
      imageUpload: false,
      videoUpload: false,
      placeholderText:
        placeholder || 'Write instructions... Use @ to reference resources.',
      charCounterCount: false,
      toolbarButtons: {
        moreText: {
          buttons: [
            'bold',
            'italic',
            'underline',
            'formatOLSimple',
            'formatUL',
          ],
          buttonsVisible: 5,
        },
        moreParagraph: {
          buttons: [],
        },
        moreRich: {
          buttons: ['insertLink'],
          align: 'center',
          buttonsVisible: 1,
        },
      },
      events: {
        initialized: () => {
          setIsFroalaInitialized(true);
        },
      },
      heightMin: 150,
      key: process.env.REACT_APP_FROALA_ACTIVATION_KEY,
    }),
    [locale, placeholder]
  );

  if (!currentOrganizationId) {
    return null;
  }

  return (
    <FroalaEditor
      // biome-ignore lint/suspicious/noExplicitAny: FroalaEditor ref typing mismatch
      ref={ref as any}
      model={model}
      onModelChange={onModelChange}
      tag="textarea"
      config={{
        ...consts.FROALA_CONFIG_DEFAULTS,
        ...config,
      }}
    />
  );
};

const mapStateToProps = (state: ReduxState) => ({
  currentOrganizationId: state.currentOrganization?.id,
});

export default connect(mapStateToProps)(React.memo(ResourceMentionEditor));
